import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogContent } from "design-system/atoms/Dialog"
import { cn } from "design-system/utils"
import { useCustomerImportStatusQuery } from "modules/customers/hooks/useCustomerImportStatusQuery"
import { Loading } from "modules/shared/components/Loading"
import { ReactNode, useState } from "react"
import { FieldValues, type Resolver, useForm, UseFormReturn } from "react-hook-form"
import {
  ImportCustomersResponseData,
  ImportCustomersSchema,
  ImportStatusResponseDataSchema,
} from "schema"
import { z } from "zod"
import { ImportFailedStep } from "./ImportFailedStep"
import { ImportProgress } from "./ImportProgress"
import { ImportReview } from "./ImportReview"
import { ImportUpload } from "./ImportUpload"

type ImportState = z.infer<typeof ImportStatusResponseDataSchema>
export type CompletedImportState = Extract<ImportState, { status: "COMPLETED" }>

export type ImportStartProps = {
  fileForm: UseFormReturn<FieldValues>
  onActionResponse: (response: z.infer<typeof ImportCustomersResponseData>) => void
}

type Props = {
  importId: string | undefined
  campaignId?: string
  className?: string
  onClose: () => void
  onImportIdChange: (next: string | undefined) => void
  renderStart?: (props: ImportStartProps) => ReactNode
  renderComplete: (state: CompletedImportState, importId: string) => ReactNode
}

export function ImportFlow({
  importId,
  campaignId,
  className,
  onClose,
  onImportIdChange,
  renderStart,
  renderComplete,
}: Props) {
  const [actionResponse, setActionResponse] =
    useState<z.infer<typeof ImportCustomersResponseData>>()

  const fileForm = useForm<FieldValues>({
    resolver: zodResolver(ImportCustomersSchema) as unknown as Resolver<FieldValues>,
    defaultValues: {},
  })

  const { data: importState, isPending: isImportStatePending } =
    useCustomerImportStatusQuery(importId)

  function clearImportId() {
    onImportIdChange(undefined)
    setActionResponse(undefined)
  }

  function renderContent() {
    if (importId) {
      if (isImportStatePending) return <Loading />

      if (!importState) {
        return (
          <ImportFailedStep
            errorMessage={null}
            onReset={clearImportId}
            onClose={onClose}
            variant="notFound"
          />
        )
      }

      if (importState.status === "FAILED") {
        return (
          <ImportFailedStep
            errorMessage={importState.errorMessage}
            onReset={clearImportId}
            onClose={onClose}
            variant="failed"
          />
        )
      }

      if (importState.status === "COMPLETED") {
        return renderComplete(importState, importId)
      }

      return <ImportProgress importId={importId} onClose={onClose} />
    }

    if (!actionResponse) {
      if (renderStart) return renderStart({ fileForm, onActionResponse: setActionResponse })

      return (
        <ImportUpload fileForm={fileForm} onClose={onClose} onActionResponse={setActionResponse} />
      )
    }

    if ("data" in actionResponse) {
      return (
        <ImportReview
          fileForm={fileForm}
          campaignId={campaignId}
          actionResponse={actionResponse}
          onBack={() => setActionResponse(undefined)}
          onActionResponse={setActionResponse}
          onQueued={(queuedImportId) => {
            setActionResponse(undefined)
            onImportIdChange(queuedImportId)
          }}
        />
      )
    }

    return null
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent
        className={cn(
          className,
          actionResponse &&
            !importId &&
            "data" in actionResponse &&
            "flex flex-col overflow-hidden sm:max-w-screen-xl",
        )}
      >
        {renderContent()}
      </DialogContent>
    </Dialog>
  )
}
